import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { CssTextField } from 'components/select';
import Autocomplete from '@mui/material/Autocomplete';
import { useMutation } from 'react-query';
import axios, { endPoints } from 'api/request';
import throttle from 'lodash/throttle';
import { useRecoilState } from 'recoil';
import { OrderPayload, validOnboardingForm } from 'pages/project-onboarding/store';
import SearchIcon from '@mui/icons-material/Search';
import ProjectOnboardingStyles from 'pages/project-onboarding/style';
import AddMember from 'pages/project-onboarding/components/Form/ServiceMembers/AddMember';
import HelpImg from 'pages/project-onboarding/components/helpImg';

const TestPlanUser = () => {
    const [order, setOrder] = useRecoilState(OrderPayload);
    const [value, setValue] = React.useState(null);
    const [ccInputValue, setCCInputValue] = React.useState('-');
    const [options, setOptions] = React.useState([]);
    const [searched, setSearched] = React.useState(false);
    const [isValidForm, setValidForm] = useRecoilState(validOnboardingForm);

    const memberApi = useMutation(() => axios.get(`${endPoints.day2Operation?.ProjectOnboarding?.member}?key=${ccInputValue}`));
    const testPlanMemberPostApi = useMutation((payload) => axios.get(`${endPoints.day2Operation?.ProjectOnboarding?.member}?key=${payload}`));

    const setTestPlanUser = (testPlanUser, testPlanUserDetails) => {
        setOrder({
            ...order,
            testPlanUser,
            testPlanUserDetails,
        });
    };

    React.useEffect(() => {
        if (!order?.testPlanUser && !order?.testPlanUserDetails) {
            setOptions([]);
        }
    }, [order?.testPlanUser, order?.testPlanUserDetails]);

    const resetOptions = () => {
        setOptions([]);
        setTestPlanUser(null, null);
    };

    const fetch = React.useMemo(
        () =>
            throttle(() => {
                memberApi.mutate();
            }, 1000),
        []
    );
    const handleSearch = () => {
        // istanbul ignore if
        if (ccInputValue === '') {
            resetOptions();
        }
        // istanbul ignore else
        if (ccInputValue !== '' && ccInputValue.length > 2) {
            setSearched(true);
            fetch();
        }
    };

    const isExistsInOptions = (testPlanUserValue) => options.find((option) => testPlanUserValue === option.name);

    React.useEffect(() => {
        if (options && options?.length === 1 && !order?.testPlanUser && order?.testPlanUserDetails) {
            setOrder({ ...order, testPlanUser: options[0]?.label, testPlanUserDetails: options?.[0] });
        }
        if (options && options?.length > 1 && order?.testPlanUserDetails && !order?.testPlanUser) {
            const testPlanUserObj = isExistsInOptions(order?.testPlanUserDetails?.name);
            setOrder({ ...order, testPlanUser: options[0]?.label, testPlanUserDetails: testPlanUserObj });
        }
    }, [options]);

    React.useEffect(() => {
        if (memberApi?.data?.data) {
            const combinedValue = memberApi?.data?.data?.map((app) => ({
                label: `${app?.gpid} - ${app?.name}`,
                gpid: `${app?.gpid}`,
                name: `${app?.name}`,
                email: app?.email,
            }));
            setOptions(combinedValue);
        }
    }, [memberApi?.data?.data]);

    React.useEffect(() => {
        if (testPlanMemberPostApi?.data?.data) {
            const combinedValue = testPlanMemberPostApi?.data?.data?.map((app) => ({
                label: `${app.gpid} - ${app.name}`,
                gpid: `${app.gpid}`,
                name: `${app.name}`,
                email: app.email,
            }));
            setOptions(combinedValue);
        }
    }, [testPlanMemberPostApi?.data?.data]);

    React.useEffect(() => {
        if (value === null) {
            // resetOptions();
        } else {
            setOptions(options?.filter((z) => z?.gpid === value?.gpid));
            setTestPlanUser(value?.label, value);
        }
    }, [value]);

    React.useEffect(() => {
        if (order?.testPlanUser && !order?.testPlanUserDetails) {
            testPlanMemberPostApi.mutate(order?.testPlanUser);
        }
    }, [order?.testPlanUser, order?.testPlanUserDetails]);

    const validateMember = (member) => {
        const duplicate = [...order?.testPlanUserList, ...order?.removedTestPlanUserList]?.filter((item) => item?.gpid === member?.gpid);
        if (duplicate && duplicate?.length) {
            setValidForm({ ...isValidForm, isDuplicateTestPlanUser: true });
        } else {
            setValidForm({ ...isValidForm, isDuplicateTestPlanUser: false });
        }
    };

    const handleAddMember = () => {
        if (order?.testPlanUserList?.length > 4) {
            setValidForm({ ...isValidForm, isTestPlanMaxMemberLimit: true });
        } else {
            setValidForm({ ...isValidForm, isTestPlanMaxMemberLimit: false });
            // eslint-disable-next-line
            if (!isValidForm?.isDuplicateTestPlanUser) {
                setOrder({
                    ...order,
                    testPlanUserList: [...order.testPlanUserList, order?.testPlanUserDetails],
                    testPlanUser: null,
                    testPlanUserDetails: null,
                });
            }
        }
    };

    const getHelperText = () => {
        let errorText;
        if (isValidForm?.isDuplicateTestPlanUser) {
            errorText = 'Member already present in the Add Member or Remove Member list.';
        } else if (isValidForm?.isTestPlanMaxMemberLimit) {
            errorText = 'You have reached max limit of 5 members.';
        }
        return errorText;
    };

    const getNoOptionText = () => {
        let text = '';
        if (searched === false) {
            text = 'Type & press enter to search by GPID, Firstname and Lastname';
        } else if (options?.length === 0) {
            text = 'No results found';
        }
        return text;
    };

    return (
        <>
            {' '}
            <Box display="flex" alignItems="flex-start" gap={1}>
                <Autocomplete
                    key="member"
                    size="small"
                    noOptionsText={getNoOptionText()}
                    includeInputInList
                    loading={memberApi?.isLoading}
                    value={order?.testPlanUser || null}
                    onInputChange={(event, newInputValue) => {
                        if (newInputValue) setCCInputValue(newInputValue?.includes('-') ? newInputValue?.split('-')?.pop()?.trim() : newInputValue);
                    }}
                    onChange={(event, newValue) => {
                        // istanbul ignore else
                        if (typeof newValue === 'string') {
                            setValue({
                                label: newValue,
                            });
                        } else if (newValue && newValue.inputValue) {
                            // Create a new value from the user input
                            setValue({
                                label: newValue.inputValue,
                            });
                        } else {
                            setValue(newValue);
                            validateMember(newValue);
                        }
                    }}
                    isOptionEqualToValue={(option, value1) => option.id === value1.id}
                    renderOption={(props, option) => (
                        <li {...props} key={option?.gpid}>
                            {option?.label}
                        </li>
                    )}
                    onBlur={() => {
                        if (!value || !order?.testPlanUser) {
                            setSearched(false);
                            setOptions([]);
                        }
                    }}
                    onKeyUp={(event) => {
                        if (event.key === 'Enter') {
                            setSearched(true);
                            handleSearch();
                        }
                        if ((event.key === 'Backspace' || event.key === 'Delete') && (!event.target.value || event.target.value === '')) {
                            setSearched(false);
                            resetOptions();
                            setValidForm({ ...isValidForm, isDuplicateTestPlanUser: false, isTestPlanMaxMemberLimit: false });
                        }
                    }}
                    filterOptions={(optionValues, params) => {
                        // eslint-disable-next-line no-underscore-dangle
                        const _results = [];
                        // eslint-disable-next-line no-plusplus
                        for (let i = 0; i < optionValues.length; i++) {
                            // eslint-disable-next-line no-restricted-syntax
                            for (const key in optionValues[i]) {
                                if (optionValues[i][key] !== null && typeof optionValues[i][key] !== 'boolean' && typeof optionValues[i][key] !== 'number') {
                                    if (optionValues[i][key].toLowerCase().includes(params?.inputValue.toLowerCase())) {
                                        _results.push(optionValues[i]);
                                    }
                                }
                            }
                        }
                        return [...new Set(_results)];
                        // return _results
                    }}
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    disableClearable
                    id="free-solo-with-text-demo"
                    options={options}
                    sx={{ width: '80%' }}
                    renderInput={(params) => (
                        <CssTextField
                            size="large"
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            {...params}
                            label="Test Plan Users *"
                            helperText={<span style={{ color: '#d40020' }}>{getHelperText()}</span>}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        <SearchIcon
                                            sx={ProjectOnboardingStyles.MemberAction.searchIcon}
                                            onClick={() => {
                                                handleSearch();
                                                setSearched(true);
                                            }}
                                            data-testid="search-btn"
                                        />
                                    </>
                                ),
                            }}
                            className={order?.isTestPlanMaxMemberLimit?.length === 0 && order?.isTouched ? 'validate' : ''}
                            fullWidth
                        />
                    )}
                />

                <AddMember handleAddMember={() => handleAddMember()} action="addTestplanMembers" />
                <Box mt={0.5}>
                    <HelpImg
                        maxWidth="500px"
                        title={
                            <>
                                <Box sx={ProjectOnboardingStyles.helpImgStyle.wrapper}>
                                    <Box sx={ProjectOnboardingStyles.helpImgStyle.namingRuleWrapper}>
                                        <Grid container spacing={1}>
                                            <Grid item xs={12} sx={ProjectOnboardingStyles.helpImgStyle.namingRule}>
                                                <Typography sx={ProjectOnboardingStyles.helpImgStyle.typographyStyle}>
                                                    <b>Test plans</b> incur license costs, so it’s essential to <b>add only users</b> who will actively{' '}
                                                    <b>define and manage test plans, test suites, and test cases</b>. By doing so, you can optimize your
                                                    resources and ensure efficient utilization of licenses.
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Box>
                            </>
                        }
                    />
                </Box>
            </Box>
        </>
    );
};

export default React.memo(TestPlanUser);
